import React, { useEffect, useState } from 'react';
import { func, bool, string, shape } from 'prop-types';
import { validate as validator } from 'ui-validator';
import { heapTrackErrors } from '@shared_modules/baui-heap-tracking';
import { PLTextField, EmailInput } from '@shared_modules/local-propulsion/components';
import { Page } from '../../components';
import validations from './name-validations';
import SecurityNotice from '../../components/SecurityNotice';
import { CoborrowerConsent } from '@shared_modules/baui-ui/components';
import TealiumTrack from '@shared_modules/baui-utils/client/utils/tealium-track';
import { trackAppStart } from '../../utils/util';

const Name = ({
  firstName: initialFirstName,
  lastName: initialLastName,
  emailSecondary: initialEmailSecondary,
  email,
  isSecondary,
  isSecondLoan,
  saveCoApplicantEmail,
  saveValues,
  nextLocation,
  previousLocation,
  captureEmail,
  isFromSecondLoanFlow,
  dm,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [emailSecondary, setEmailSecondary] = useState(initialEmailSecondary);
  const [validationErrors, setValidationErrors] = useState({});
  const showNotificationBanner = !isFromSecondLoanFlow && !isSecondary;
  const goBackFn = !isFromSecondLoanFlow && !isSecondary ? null : previousLocation;

  const content = {
    primary: {
      title: 'What’s your name?',
      header: 'This helps us verify your identity and create your customized offer.',
    },
    secondary: {
      title: 'What’s your co-borrower’s name?',
      header: 'Let’s get to know your co-borrower.',
    },
  };

  const pageContent = content[isSecondary ? 'secondary' : 'primary'];

  useEffect(() => {
    if (!isFromSecondLoanFlow) trackAppStart({ isDm: dm.isDm });
  }, []);

  const validateInput = fieldName => {
    const hasCoApplicantEmail = isSecondary && isSecondLoan;
    // extracting out other errors than error for the current field
    const { [fieldName]: omit, ...restOfValidationErrors } = validationErrors;
    const errors = validator(
      { firstName, lastName, emailSecondary, primaryEmail: email },
      validations(fieldName, hasCoApplicantEmail)
    );
    if (fieldName) {
      const mergedErrors = {
        ...restOfValidationErrors,
        ...errors,
      };
      return mergedErrors;
    }

    return errors;
  };

  const handleInputChange = event => {
    const { name, value } = event.target;
    if (name === 'emailSecondary') setEmailSecondary(value);
    else {
      if (/\d/.test(value)) return;
      if (name === 'firstName') setFirstName(value);
      if (name === 'lastName') setLastName(value);
    }
  };

  const handleBlur = event => {
    const name = event.target.name;
    if (name === 'emailSecondary') {
      setValidationErrors({
        ...validationErrors,
        [name]: event.target.validationErrors[name],
      });
    } else {
      const errors = validateInput(event.target.name);
      setValidationErrors(errors);
    }
  };

  const submit = e => {
    if (e) e.preventDefault();
    const errors = validateInput();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      heapTrackErrors(errors);
    } else {
      if (!isFromSecondLoanFlow) TealiumTrack.link({ PI1_Start: 'true' });
      if (emailSecondary) saveCoApplicantEmail(emailSecondary);
      saveValues(
        {
          name: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          },
        },
        nextLocation
      );
    }
  };

  return (
    <Page
      title={pageContent.title}
      header={pageContent.header}
      name="name"
      goBack={goBackFn}
      submitHandler={submit}
      showDisclaimerContent={false}
      showPatriotActDisclosures={!isSecondary}
      showNotificationBanner={showNotificationBanner}
    >
      <div className="form__input">
        <PLTextField
          name="firstName"
          inputProps={{
            maxLength: 40,
          }}
          value={firstName}
          autoFocus={!firstName}
          autoComplete="given-name"
          onBlur={handleBlur}
          onChange={handleInputChange}
          label="First Name"
          errors={validationErrors.firstName}
        />
      </div>
      <div className="form__input">
        <PLTextField
          name="lastName"
          inputProps={{
            maxLength: 40,
          }}
          value={lastName}
          autoComplete="family-name"
          onBlur={handleBlur}
          onChange={handleInputChange}
          label="Last Name"
          errors={validationErrors.lastName}
        />
      </div>
      {isSecondary && isSecondLoan && (
        <>
          <EmailInput
            captureEmail={captureEmail}
            defaultValue={emailSecondary}
            firstName={firstName}
            isJoint={isSecondary}
            label="Email Address"
            name="emailSecondary"
            onBlur={handleBlur}
            onChange={handleInputChange}
            primaryEmail={email}
            validationErrors={validationErrors}
            isCoApplicant
          />
          <CoborrowerConsent variant="consentEmail" className="CoborrowerConsentNotice--marginTop" />
        </>
      )}
      <SecurityNotice text="We know that your information is personal, so we use bank-level encryption at all times." />
    </Page>
  );
};

Name.propTypes = {
  firstName: string,
  lastName: string,
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  isSecondary: bool.isRequired,
  email: string,
  emailSecondary: string,
  captureEmail: func,
  isSecondLoan: bool,
  saveCoApplicantEmail: func,
  isFromSecondLoanFlow: bool,
  dm: shape({}),
};

Name.defaultProps = {
  firstName: '',
  lastName: '',
  state: '',
  loanAmount: '',
  email: null,
  emailSecondary: null,
  captureEmail: () => {},
  isSecondLoan: false,
  saveCoApplicantEmail: () => {},
  dm: {},
};

export default Name;

